/*eslint no-undef: "off"*/

import { useQuery, keepPreviousData, QueryClient } from '@tanstack/vue-query'
import { useQueryItem } from '@/composables/query/item/useQueryItem'
import { removeEmptyProperties } from '@/utilities/removeEmptyProperties'

const q = useQueryItem('q', null)
export const locationNameSearchQ = q

// ---------------------------------------------------------------------------
// AHRQ / HCRIS filters — refine the hospital name typeahead (moved off the
// hospital rate query). Declared at module scope so state is shared across the
// component tree and this querier without prop drilling.
// ---------------------------------------------------------------------------
const asInt = (value) => {
    const parsed = parseInt(value, 10)
    return value !== null && value !== '' && !Number.isNaN(parsed) ? parsed : null
}
const asFloat = (value) => (value !== null && value !== '' ? parseFloat(value) : null)

// Boolean "only show" toggles
export const isAcuteCareHospital = useQueryItem('is_acute_care_hospital', null, {
    label: 'Only Show Acute Care Hospitals',
})
export const isChildrensHospital = useQueryItem('is_childrens_hospital', null, {
    label: "Only Show Children's Hospitals",
})
export const isMajorTeachingHospital = useQueryItem('is_major_teaching_hospital', null, {
    label: 'Only Show Major Teaching Hospitals',
})
export const isVeryMajorTeachingHospital = useQueryItem('is_very_major_teaching_hospital', null, {
    label: 'Only Show Very Major Teaching Hospitals',
})
export const hasHighDshPatientPercentage = useQueryItem('has_high_dsh_patient_percentage', null, {
    label: 'Only Show High DSH Patient %',
})
export const hasHighUncompensatedCareBurden = useQueryItem('has_high_uncompensated_care_burden', null, {
    label: 'Only Show High Uncompensated Care Burden',
})

// Numeric range filters
export const minHospitalBedCount = useQueryItem('min_hospital_bed_count', null, {
    transform: asInt,
    label: 'Hospital Bed Count Min',
})
export const maxHospitalBedCount = useQueryItem('max_hospital_bed_count', null, {
    transform: asInt,
    label: 'Hospital Bed Count Max',
})
export const minAnnualDischargeCount = useQueryItem('min_annual_discharge_count', null, {
    transform: asInt,
    label: 'Annual Discharge Count Min',
})
export const maxAnnualDischargeCount = useQueryItem('max_annual_discharge_count', null, {
    transform: asInt,
    label: 'Annual Discharge Count Max',
})
export const minFteInternResidentCount = useQueryItem('min_fte_intern_resident_count', null, {
    transform: asInt,
    label: 'FTE Intern & Resident Count Min',
})
export const maxFteInternResidentCount = useQueryItem('max_fte_intern_resident_count', null, {
    transform: asInt,
    label: 'FTE Intern & Resident Count Max',
})
export const minResidentToBedRatio = useQueryItem('min_resident_to_bed_ratio', null, {
    transform: asFloat,
    label: 'Resident to Bed Ratio Min',
})
export const maxResidentToBedRatio = useQueryItem('max_resident_to_bed_ratio', null, {
    transform: asFloat,
    label: 'Resident to Bed Ratio Max',
})
export const minUncompensatedCareBurden = useQueryItem('min_uncompensated_care_burden', null, {
    transform: asFloat,
    label: 'Uncompensated Care Burden Min',
})
export const maxUncompensatedCareBurden = useQueryItem('max_uncompensated_care_burden', null, {
    transform: asFloat,
    label: 'Uncompensated Care Burden Max',
})
export const minNetPatientRevenue = useQueryItem('min_net_patient_revenue', null, {
    transform: asFloat,
    label: 'Net Patient Revenue Min',
})
export const maxNetPatientRevenue = useQueryItem('max_net_patient_revenue', null, {
    transform: asFloat,
    label: 'Net Patient Revenue Max',
})
export const minTotalPatientRevenue = useQueryItem('min_total_patient_revenue', null, {
    transform: asFloat,
    label: 'Total Patient Revenue Min',
})
export const maxTotalPatientRevenue = useQueryItem('max_total_patient_revenue', null, {
    transform: asFloat,
    label: 'Total Patient Revenue Max',
})

// Categorical / text filters
export const ownershipType = useQueryItem('ownership_type', null, { label: 'Ownership Type' })
export const corporateParentName = useQueryItem('corporate_parent_name', null, { label: 'Corporate Parent Name' })
export const corporateParentType = useQueryItem('corporate_parent_type', null, { label: 'Corporate Parent Type' })
export const city = useQueryItem('city', null, { label: 'City' })
export const state = useQueryItem('state', null, { label: 'State' })

// Every AHRQ filter item, in the order they are appended to the request.
const filterItems = [
    isAcuteCareHospital,
    isChildrensHospital,
    isMajorTeachingHospital,
    isVeryMajorTeachingHospital,
    hasHighDshPatientPercentage,
    hasHighUncompensatedCareBurden,
    minHospitalBedCount,
    maxHospitalBedCount,
    minAnnualDischargeCount,
    maxAnnualDischargeCount,
    minFteInternResidentCount,
    maxFteInternResidentCount,
    minResidentToBedRatio,
    maxResidentToBedRatio,
    minUncompensatedCareBurden,
    maxUncompensatedCareBurden,
    minNetPatientRevenue,
    maxNetPatientRevenue,
    minTotalPatientRevenue,
    maxTotalPatientRevenue,
    ownershipType,
    corporateParentName,
    corporateParentType,
    city,
    state,
]

function hasActiveFilterValue(value) {
    if (value === null || value === undefined || value === '') return false
    if (Array.isArray(value)) return value.length > 0
    return true
}

export const hasActiveLocationNameFilters = computed(() =>
    filterItems.some((item) => hasActiveFilterValue(item.query.value))
)

export const activeLocationNameFilterCount = computed(
    () => filterItems.filter((item) => hasActiveFilterValue(item.query.value)).length
)

// Resets all hospital filter items without touching the search query (q).
export function resetLocationNameFilters() {
    for (const item of filterItems) item.reset()
}

// Serialize the currently-set filters into flat query params. Nulls/empties are
// skipped, arrays (ownership_type) are comma-joined, everything else stringified.
function filterSearchParams() {
    const params = {}
    for (const item of filterItems) {
        const value = item.query.value
        if (value === null || value === undefined || value === '') continue
        if (Array.isArray(value)) {
            if (!value.length) continue
            params[item.key] = value.join(',')
        } else {
            params[item.key] = String(value)
        }
    }
    return params
}

export function useSignalQueryLocationName() {
    /**
     * Cache Key — includes every filter ref so the search refetches when a filter changes.
     */
    const queryKey = ['signal-location-name', q.query, ...filterItems.map((item) => item.query)]

    const enabled = computed(() => q.query.value?.length >= 3 || (!q.query.value && hasActiveLocationNameFilters.value))

    /**
     * Query function
     */
    async function get() {
        const { api } = useSignalAPI()

        const params = removeEmptyProperties({
            q: q.toValue(),
            ...filterSearchParams(),
        })

        const { results, meta } = await api(`/locations/search/v1?${new URLSearchParams(params)}`)

        return { results, meta }
    }

    function resetQuery() {
        const queryClient = new QueryClient()

        queryClient.removeQueries({ queryKey })

        q.set(null)
        for (const item of filterItems) item.reset()
    }

    function clearQ() {
        q.set(null)
    }

    // Two useQuery calls sharing the same queryKey — TanStack Query dedupes them
    // into a single network request while allowing each to select a different slice.
    const query = useQuery({
        queryKey,
        queryFn: () => get(),
        enabled,
        placeholderData: keepPreviousData,
        select: (data) => data?.results ?? [],
    })

    const metaQuery = useQuery({
        queryKey,
        queryFn: () => get(),
        enabled,
        placeholderData: keepPreviousData,
        select: (data) => data?.meta ?? null,
    })

    // Cities available for the currently-selected state, derived from meta.states_cities
    const cityOptions = computed(() => {
        const selectedState = state.query.value
        if (!selectedState) return []
        const cities = metaQuery.data.value?.states_cities?.[selectedState] ?? []
        return cities.map((c) => ({
            label: c.replace(/\w+/g, (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()),
            value: c,
        }))
    })

    return {
        query,
        metaQuery,
        cityOptions,
        q,
        enabled,
        resetQuery,
        clearQ,
    }
}
